import React, { useCallback, useMemo } from "react"
import styled from "styled-components"
import { BodySmall } from "../../atoms/Bodies"
import { TextLink } from "../../atoms/links/TextLink"
import { PlanInfoBadgeContainer } from "../../../containers/planinfo/PlanInfoBadgeContainer"
import { Spacer } from "../../atoms/Atoms"
import { Body, Container, SlidingDotsLoader } from "@carrotfertility/carotene"
import { SwitchH1ToH2 } from "../../atoms/Molecules"
import { Modal } from "../../modal/Modal"
import EmployeeOnlyModalContainer from "../../../employee-only-containers/EmployeeOnlyModalContainer"
import { DbgPaths } from "../../../../utils/Paths"
import { MedicationListItems, MedicationsFooter, MedicationsHeader } from "./multiple-categories/MedicationsCategory"
import { OtherExpensesHeader, OtherExpensesListItems } from "./multiple-categories/OtherExpensesCategory"
import { QmeHeader, QmeListItems } from "./multiple-categories/QmeCategory"
import useBenefit from "../../../dynamic-content/hooks/useGetBenefit"
import { FinancialSupportErrorMessage } from "./FinancialSupportErrorMessage"
import { getBenefitConfiguration } from "../../../../reducers/benefitConfiguration"
import { useSelector } from "react-redux"
import { getCountryCode } from "../../../../reducers/companyInfo"
import useCompanyComplianceConfigLineItemFilter from "../hooks/useGetCompanyComplianceConfigLineItemFilter"
import { UnorderedList, UnorderedListItem } from "@carrotfertility/carotene-core"
import { FormattedMessage } from "react-intl"

const BenefitDisplayPlanTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 600px;
`

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 80%;
  align-self: center;
`

const ModalSectionHeaders = styled.span`
  font-weight: 500;
`

const PlanInfoBadgeMarginWrapper = styled.div`
  margin-left: 1rem;
  margin-bottom: 0.5rem;
`

// @ts-expect-error TS7006
export const PayingForCareContentSub = (props) => {
  const { categoryName, benefitInfo, lineItemFilter } = props
  const isOtherExpenses = useMemo(() => categoryName === "Other expenses", [categoryName])
  const preservation = benefitInfo.journeys.fertilityPreservation
  const fertilityCare = benefitInfo.journeys.fertilityCare
  const hasElectiveFertilityLineItemFilter = lineItemFilter === "ELECTIVE_FERTILITY"
  const showQmeInfertilityMessage =
    fertilityCare.eligibleExpensesDetail.isCoveredWithInfertilityDiagnosis ||
    preservation.eligibleExpensesDetail.isMedicallyNecessaryPreservationCovered
  const showOtherExpensesInfertilityMessage =
    fertilityCare.eligibleExpensesDetail.isElectiveCareCovered ||
    preservation.eligibleExpensesDetail.isElectivePreservationCovered

  return (
    <>
      {!hasElectiveFertilityLineItemFilter && (showQmeInfertilityMessage || showOtherExpensesInfertilityMessage) && (
        <div>
          <ModalSectionHeaders>
            <FormattedMessage defaultMessage="How do I know if I have an infertility diagnosis?" />
          </ModalSectionHeaders>
          <Spacer height={0.5} />
          <Body color="black-80">
            <p>
              <FormattedMessage
                defaultMessage={
                  "If you’re not sure, ask your fertility doctor. Based on local standards" +
                  " of practice, a doctor may diagnose infertility if pregnancy does not occur within a" +
                  " specific timeframe of unprotected intercourse or insemination."
                }
              />
            </p>
            <Spacer height={0.5} />
            <p>
              <FormattedMessage defaultMessage="An infertility diagnosis may also be issued due to:" />
            </p>
          </Body>
          <Spacer height={0.5} />
          <UnorderedList>
            <UnorderedListItem>
              <p className="lh-copy">
                <FormattedMessage defaultMessage="Ovulation issues or diminished ovarian reserve" />
              </p>
            </UnorderedListItem>
            <UnorderedListItem>
              <p className="lh-copy">
                <FormattedMessage defaultMessage="Problems with semen, sperm, or ejaculation" />
              </p>
            </UnorderedListItem>
            <UnorderedListItem>
              <p className="lh-copy">
                <FormattedMessage defaultMessage="Anatomical, endocrine, genetic, or functional differences in the reproductive system" />
              </p>
            </UnorderedListItem>
          </UnorderedList>
        </div>
      )}
      <Spacer height={1.0} />
      {!isOtherExpenses &&
        !hasElectiveFertilityLineItemFilter &&
        preservation.eligibleExpensesDetail.isMedicallyNecessaryPreservationCovered && (
          <div>
            <ModalSectionHeaders>
              <FormattedMessage defaultMessage="How do I know if my preservation care is medically necessary?" />
            </ModalSectionHeaders>
            <Spacer height={0.5} />
            <Body color="black-80">
              <FormattedMessage
                defaultMessage={
                  "If you’re not sure, ask your fertility doctor. Medical necessity" +
                  " for fertility preservation is determined by a doctor based on health issues or treatments that" +
                  " could significantly impact the reproductive system. For example, preservation may be considered " +
                  "medically necessary before undergoing certain types of chemotherapy or surgery."
                }
              />
            </Body>
          </div>
        )}
      {isOtherExpenses &&
        !hasElectiveFertilityLineItemFilter &&
        preservation.eligibleExpensesDetail.isElectivePreservationCovered && (
          <div>
            <ModalSectionHeaders>
              <FormattedMessage defaultMessage="How do I know if my care is medically necessary?" />
            </ModalSectionHeaders>
            <Spacer height={0.5} />
            <Body color="black-80">
              <FormattedMessage
                defaultMessage={
                  "If you’re not sure, ask your fertility doctor. Medical necessity" +
                  " for fertility preservation is determined by a doctor based on health issues or treatments that" +
                  " could significantly impact the reproductive system. For example, preservation may be considered " +
                  "medically necessary before undergoing certain types of chemotherapy or surgery."
                }
              />
            </Body>
          </div>
        )}
    </>
  )
}

// @ts-expect-error TS7006
const PayingForCareContentMain = (props): JSX.Element => {
  const { categoryName, hasAdoptionCategory } = props
  const isOtherExpenses = useMemo(() => categoryName === "Other expenses", [categoryName])
  const isMedicationExpenses = useMemo(() => categoryName === "Medication expenses", [categoryName])
  const { data: benefit, isLoading } = useBenefit()
  const { coverageConfig } = useSelector(getBenefitConfiguration)
  const companyGeo = useSelector(getCountryCode)
  const { data } = useCompanyComplianceConfigLineItemFilter()

  if ((data == null || benefit == null) && !isLoading) {
    return <FinancialSupportErrorMessage />
  }

  return isLoading ? (
    <SlidingDotsLoader />
  ) : (
    <ModalContainer>
      <Container padding="none" stack="large">
        <SwitchH1ToH2>{categoryName}</SwitchH1ToH2>
        <Spacer height={1.0} />
        <div>
          <Body color="black-80">
            {!isOtherExpenses && !isMedicationExpenses && <QmeHeader companyCoverage={coverageConfig} />}
            {!isOtherExpenses && isMedicationExpenses && <MedicationsHeader />}
            {isOtherExpenses && <OtherExpensesHeader />}
          </Body>
          <Spacer height={0.5} />
          <UnorderedList>
            {!isOtherExpenses && isMedicationExpenses && <MedicationListItems benefitInfo={benefit} />}
            {!isOtherExpenses && !isMedicationExpenses && (
              <QmeListItems benefitInfo={benefit} country={companyGeo} lineItemFilter={data} />
            )}
            {isOtherExpenses && (
              <OtherExpensesListItems
                benefitInfo={benefit}
                country={companyGeo}
                lineItemFilter={data}
                companyCoverage={coverageConfig}
                hasAdoptionCategory={hasAdoptionCategory}
              />
            )}
          </UnorderedList>
          <Spacer height={0.5} />
          <Body color="black-80">
            {!isOtherExpenses && isMedicationExpenses && <MedicationsFooter benefitInfo={benefit} />}
          </Body>
        </div>
        <Spacer height={1.0} />
        {!isMedicationExpenses && (
          <PayingForCareContentSub categoryName={categoryName} benefitInfo={benefit} lineItemFilter={data} />
        )}
      </Container>
    </ModalContainer>
  )
}

// @ts-expect-error TS7031
export function PayingForCareCoverageModal({ element, showLinks, hasAdoptionCategory = false }): JSX.Element {
  const [showModal, setShowModal] = React.useState(false)
  const handleOpen = useCallback(() => {
    setShowModal(true)
  }, [])
  const handleExit = useCallback(() => {
    setShowModal(false)
  }, [])
  const isAdoption = useMemo(
    () => element.reimbursementCategoryName.toLowerCase().includes("adoption"),
    [element.reimbursementCategoryName]
  )
  const supportsDeductibles = useMemo(
    () => ["adoption", "gestation", "qualified"].some((name) => element.reimbursementCategoryName.includes(name)),
    [element.reimbursementCategoryName]
  )

  return (
    <BenefitDisplayPlanTextContainer>
      {element.length > 1 && <body className="mr3 ml3 black-80"> {element.reimbursementCategoryName} </body>}
      {!isAdoption && (
        <BodySmall color="mr3 ml3 black-60">
          {showLinks && (
            <p>
              <FormattedMessage
                defaultMessage="<link>Learn</link> what this includes"
                values={{
                  link: (linkContent) => <TextLink onClick={handleOpen}>{linkContent}</TextLink>
                }}
              />
            </p>
          )}
          {showModal && (
            <EmployeeOnlyModalContainer onClose={handleExit}>
              <Modal onExit={handleExit} titleText={element.reimbursementCategoryName}>
                <PayingForCareContentMain
                  categoryName={element.reimbursementCategoryName}
                  hasAdoptionCategory={hasAdoptionCategory}
                />
                <Spacer height={3.0} />
              </Modal>
            </EmployeeOnlyModalContainer>
          )}
          <PlanInfoBadgeMarginWrapper>
            <PlanInfoBadgeContainer
              backgroundColor="light-grey"
              fontColor="black-60"
              subsidy={element}
              categorySupportsDeductibles={supportsDeductibles}
            />
          </PlanInfoBadgeMarginWrapper>
        </BodySmall>
      )}
      {isAdoption && showLinks && (
        <BodySmall color="mr3 ml3 black-60">
          <FormattedMessage
            defaultMessage="<link>Learn</link> what this includes"
            values={{
              link: (linkContent) => <TextLink to={DbgPaths.ADOPTION_COVERAGE}>{linkContent}</TextLink>
            }}
          />
          <PlanInfoBadgeContainer
            backgroundColor="light-grey"
            fontColor="black-60"
            subsidy={element}
            categorySupportsDeductibles={supportsDeductibles}
          />
        </BodySmall>
      )}
    </BenefitDisplayPlanTextContainer>
  )
}
